import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type MarketRentalsResponse } from '../api/client';
import { Loading } from '../components/common';
import './RentalsPage.css';

export function RentalsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [market, setMarket] = useState(searchParams.get('market') || '');
  const [data, setData] = useState<MarketRentalsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [markets, setMarkets] = useState<Array<{ market: string; name: string }>>([]);

  useEffect(() => {
    loadMarkets();
  }, []);

  useEffect(() => {
    if (market) {
      loadRentals(market);
      setSearchParams({ market });
    }
  }, [market]);

  const loadMarkets = async () => {
    try {
      const res = await api.getStoredMarkets();
      setMarkets(res.markets.map((m) => ({ market: m.market, name: m.name })));
    } catch (err) {
      console.error('Failed to load markets:', err);
    }
  };

  const loadRentals = async (marketSlug: string) => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getMarketRentals(marketSlug);
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rentals');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val: number) => `$${val.toLocaleString()}`;

  const getSaturationClass = (level?: string) => {
    if (!level) return '';
    const classes: Record<string, string> = {
      LOW: 'saturation-low',
      MODERATE: 'saturation-moderate',
      HIGH: 'saturation-high',
      CRITICAL: 'saturation-critical',
    };
    return classes[level] || '';
  };

  return (
    <div className="container">
      <div className="page-header">
        <div>
          <h1>Rental Market Analysis</h1>
          <p>View rental comps, saturation, and market analytics</p>
        </div>
      </div>

      <div className="filter-row">
        <select
          value={market}
          onChange={(e) => setMarket(e.target.value)}
          style={{ minWidth: 200 }}
        >
          <option value="">Select a market...</option>
          {markets.map((m) => (
            <option key={m.market} value={m.market}>
              {m.name}
            </option>
          ))}
        </select>
      </div>

      {loading && <Loading message="Loading rental data..." />}

      {error && <div className="error-message">{error}</div>}

      {data && !loading && (
        <>
          {/* Saturation Card */}
          {data.saturation && (
            <div className={`saturation-card ${getSaturationClass(data.saturation.level)}`}>
              <div className="saturation-header">
                <h3>Rental Saturation</h3>
                <span className="saturation-level">{data.saturation.level}</span>
              </div>
              <div className="saturation-stats">
                <div className="saturation-stat">
                  <div className="saturation-value">{data.saturation.score}</div>
                  <div className="saturation-label">Score</div>
                </div>
                <div className="saturation-stat">
                  <div className="saturation-value">{data.saturation.perThousandPop.toFixed(1)}</div>
                  <div className="saturation-label">Per 1K Pop</div>
                </div>
              </div>
            </div>
          )}

          {/* Analytics Summary */}
          {data.analytics && (
            <div className="analytics-grid">
              <div className="analytics-card">
                <div className="analytics-value">{formatCurrency(data.analytics.medianRent)}</div>
                <div className="analytics-label">Median Rent</div>
              </div>
              <div className="analytics-card">
                <div className="analytics-value">{formatCurrency(data.analytics.avgRent)}</div>
                <div className="analytics-label">Average Rent</div>
              </div>
              {data.analytics.rentRange && (
                <div className="analytics-card">
                  <div className="analytics-value">
                    {formatCurrency(data.analytics.rentRange.min)} - {formatCurrency(data.analytics.rentRange.max)}
                  </div>
                  <div className="analytics-label">Rent Range</div>
                </div>
              )}
              <div className="analytics-card">
                <div className="analytics-value">{data.analytics.totalListings}</div>
                <div className="analytics-label">Total Listings</div>
              </div>
            </div>
          )}

          {/* Rent by Bedroom */}
          {data.analytics?.byBedroom && Object.keys(data.analytics.byBedroom).length > 0 && (
            <div className="bedroom-breakdown">
              <h3>Rent by Bedroom Count</h3>
              <div className="bedroom-grid">
                {Object.entries(data.analytics.byBedroom)
                  .sort(([a], [b]) => Number(a) - Number(b))
                  .map(([beds, info]) => (
                    <div key={beds} className="bedroom-card">
                      <div className="bedroom-count">{beds} BR</div>
                      <div className="bedroom-rent">{formatCurrency(info.avgRent)}</div>
                      <div className="bedroom-listings">{info.count} listings</div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Rental Listings */}
          {data.listings && data.listings.length > 0 && (
            <>
              <h3>Rental Listings ({data.listings.length})</h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Address</th>
                    <th>Rent</th>
                    <th>Beds</th>
                    <th>Baths</th>
                    <th>Sqft</th>
                    <th>$/Sqft</th>
                    <th>Days Listed</th>
                  </tr>
                </thead>
                <tbody>
                  {data.listings.slice(0, 50).map((listing, i) => (
                    <tr key={i}>
                      <td>{listing.address}</td>
                      <td className="rent-cell">{formatCurrency(listing.rent)}</td>
                      <td>{listing.beds}</td>
                      <td>{listing.baths}</td>
                      <td>{listing.sqft?.toLocaleString() || '-'}</td>
                      <td>
                        {listing.sqft
                          ? `$${(listing.rent / listing.sqft).toFixed(2)}`
                          : '-'}
                      </td>
                      <td>{listing.daysOnMarket ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.listings.length > 50 && (
                <p className="table-footer">
                  Showing 50 of {data.listings.length} listings
                </p>
              )}
            </>
          )}
        </>
      )}

      {!market && !loading && (
        <div className="empty-state">
          <p>Select a market above to view rental analysis</p>
        </div>
      )}
    </div>
  );
}
